import {
  Alert,
  Button,
  Checkbox,
  Chip,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { changeListings, listIntegrationListings, resendListings } from '../../api/business/endpoints';
import type {
  IntegrationLink,
  IntegrationListing,
  ListingSelection,
  ListingState,
} from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDateTime } from '../../components/format';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { sortRows, useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

import { PublishListingsDialog } from './PublishListingsDialog';

type ListingSortKey = 'productName' | 'published' | 'state' | 'lastSentAt';

type Visibility = 'published' | 'hidden' | '';

const VALUE_OF: Record<ListingSortKey, (listing: IntegrationListing) => string | null> = {
  productName: (listing) => listing.productName,
  published: (listing) => (listing.published ? '1' : '0'),
  state: (listing) => listing.state,
  lastSentAt: (listing) => listing.lastSentAt,
};

const STATE_COLOR: Record<ListingState, 'default' | 'success' | 'error'> = {
  PENDING: 'default',
  SENT: 'success',
  FAILED: 'error',
};

/**
 * The products a shop lists (R0033) — the whole list, sorted in the browser: published or hidden, how each
 * document went, and the owner's publish, hide, send again and take-from-the-shop.
 */
export const IntegrationListingsTable = ({
  link,
  canManage,
}: {
  link: IntegrationLink;
  canManage: boolean;
}) => {
  const { language, t } = useLanguage();
  const [term, setTerm] = useState('');
  const [state, setState] = useState<ListingState | ''>('');
  const [visibility, setVisibility] = useState<Visibility>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [publishing, setPublishing] = useState(false);
  const [confirming, setConfirming] = useState<'resend' | 'importAll' | null>(null);
  const takesProducts = link.capabilities.includes('products');
  const canTake = takesProducts && link.capabilities.includes('import');
  const active = link.status === 'ACTIVE' && link.firstSyncState === 'DONE';
  const sort = useSort<ListingSortKey>('productName');
  const query = useQuery({
    queryKey: ['integration-listings', link.id],
    queryFn: () => listIntegrationListings(link.companyId, link.id),
    // While documents wait for the next delivery the owner watches them go.
    refetchInterval: (current) =>
      (current.state.data ?? []).some((one) => one.state === 'PENDING' || one.importRequestedAt !== null)
        ? 5000
        : false,
  });
  const invalidates = [
    'integration-listings',
    'integration-link',
    'integration-unlisted',
    'product-listings',
  ];
  const change = useAppMutation(changeListings, invalidates, () => {
    setSelected(new Set());
    setPublishing(false);
    setConfirming(null);
  });
  const resend = useAppMutation(resendListings, invalidates, () => setConfirming(null));
  const ids = { companyId: link.companyId, linkId: link.id };
  const act = (action: 'publish' | 'unpublish' | 'import', selection: ListingSelection) =>
    change.mutate({ ...ids, action, selection });

  const needle = term.trim().toLocaleLowerCase('sk');
  const rows = sortRows(
    (query.data ?? []).filter(
      (one) =>
        (state === '' || one.state === state) &&
        (visibility === '' || one.published === (visibility === 'published')) &&
        (needle === '' ||
          [one.productName, one.sku, one.ean, one.shopProductId].some((value) =>
            value?.toLocaleLowerCase('sk').includes(needle),
          )),
    ),
    sort.direction,
    VALUE_OF[sort.key],
  );
  const toggle = (productId: string) =>
    setSelected((current) => {
      const next = new Set(current);

      if (next.has(productId)) next.delete(productId);
      else next.add(productId);

      return next;
    });
  const chosen = [...selected];
  const manage = canManage && takesProducts && active;

  return (
    <Paper variant="outlined">
      {!takesProducts ? (
        <Alert severity="info" variant="outlined" sx={{ m: 2 }}>
          {t('listings.notSupported')}
        </Alert>
      ) : !active ? (
        <Alert severity="info" variant="outlined" sx={{ m: 2 }}>
          {t('listings.firstSyncPending')}
        </Alert>
      ) : null}
      <ErrorNotice
        error={
          query.error ?? (publishing ? null : change.error) ?? (confirming === null ? resend.error : null)
        }
        onRetry={() => void query.refetch()}
      />
      {manage ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ px: 2, pt: 2 }}>
          <Button variant="contained" onClick={() => setPublishing(true)}>
            {t('listings.publish')}
          </Button>
          <Button onClick={() => setConfirming('resend')}>{t('listings.resend')}</Button>
          {canTake ? (
            <Button onClick={() => setConfirming('importAll')}>{t('listings.importAll')}</Button>
          ) : null}
          {chosen.length > 0 ? (
            <>
              <Chip label={t('listings.selected', { count: chosen.length })} sx={{ alignSelf: 'center' }} />
              <Button disabled={change.isPending} onClick={() => act('publish', { productIds: chosen })}>
                {t('listings.publishSelected')}
              </Button>
              <Button disabled={change.isPending} onClick={() => act('unpublish', { productIds: chosen })}>
                {t('listings.unpublishSelected')}
              </Button>
              {canTake ? (
                <Button disabled={change.isPending} onClick={() => act('import', { productIds: chosen })}>
                  {t('listings.importSelected')}
                </Button>
              ) : null}
            </>
          ) : null}
        </Stack>
      ) : null}
      <TableFilters>
        <TextField
          size="small"
          label={t('common.search')}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />
        <TextField
          select
          size="small"
          label={t('field.status')}
          value={state}
          onChange={(e) => setState(e.target.value as ListingState | '')}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">{t('common.all')}</MenuItem>
          {(['PENDING', 'SENT', 'FAILED'] as const).map((value) => (
            <MenuItem key={value} value={value}>
              {t(`listingState.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('listings.visibility')}
          value={visibility}
          onChange={(e) => setVisibility(e.target.value as Visibility)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">{t('common.all')}</MenuItem>
          <MenuItem value="published">{t('listings.published')}</MenuItem>
          <MenuItem value="hidden">{t('listings.hidden')}</MenuItem>
        </TextField>
      </TableFilters>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {manage ? <TableCell padding="checkbox" /> : null}
              <SortableCell sort={sort} column="productName">
                {t('listings.product')}
              </SortableCell>
              <SortableCell sort={sort} column="published">
                {t('listings.visibility')}
              </SortableCell>
              <SortableCell sort={sort} column="state">
                {t('field.status')}
              </SortableCell>
              <SortableCell sort={sort} column="lastSentAt">
                {t('listings.lastSent')}
              </SortableCell>
              {manage ? <TableCell align="right">{t('common.actions')}</TableCell> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {query.isLoading ? (
              <TableSkeletonRows columns={manage ? 6 : 4} />
            ) : (
              rows.map((one) => (
                <TableRow key={one.productId} hover>
                  {manage ? (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selected.has(one.productId)}
                        onChange={() => toggle(one.productId)}
                      />
                    </TableCell>
                  ) : null}
                  <TableCell>
                    {one.productName ?? t('common.none')}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {[
                        one.sku,
                        one.ean,
                        one.shopProductId ? `${t('listings.shopId')} #${one.shopProductId}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  </TableCell>
                  <TableCell>{one.published ? t('listings.published') : t('listings.hidden')}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={STATE_COLOR[one.state]}
                      label={t(`listingState.${one.state}`)}
                    />
                    {one.state === 'FAILED' && one.lastError ? (
                      <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                        {one.lastError}
                      </Typography>
                    ) : null}
                    {one.skipped.length > 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t('listings.skipped', { list: one.skipped.join(', ') })}
                      </Typography>
                    ) : null}
                    {one.importRequestedAt !== null ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t('listings.importPending')}
                      </Typography>
                    ) : one.importedAt !== null ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t('listings.imported', { when: formatDateTime(one.importedAt, language) })}
                      </Typography>
                    ) : null}
                    {one.importError ? (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                        {t('listings.importError', { error: one.importError })}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{formatDateTime(one.lastSentAt, language)}</TableCell>
                  {manage ? (
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          disabled={change.isPending}
                          onClick={() =>
                            act(one.published ? 'unpublish' : 'publish', { productIds: [one.productId] })
                          }
                        >
                          {one.published ? t('listings.unpublish') : t('listings.republish')}
                        </Button>
                        {canTake && one.shopProductId !== null ? (
                          <Button
                            size="small"
                            color="inherit"
                            disabled={change.isPending || one.importRequestedAt !== null}
                            onClick={() => act('import', { productIds: [one.productId] })}
                          >
                            {t('listings.take')}
                          </Button>
                        ) : null}
                      </Stack>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!query.isLoading && rows.length === 0 ? (
        <EmptyState title={t('listings.empty')} detail={t('listings.emptyDetail')} />
      ) : null}
      {publishing ? (
        <PublishListingsDialog
          link={link}
          listed={new Set((query.data ?? []).map((one) => one.productId))}
          onClose={() => setPublishing(false)}
          onSubmit={(selection) => act('publish', selection)}
          isBusy={change.isPending}
          error={change.error}
        />
      ) : null}
      {confirming !== null ? (
        <ConfirmDialog
          title={confirming === 'resend' ? t('listings.resendTitle') : t('listings.importAllTitle')}
          message={confirming === 'resend' ? t('listings.resendMessage') : t('listings.importAllMessage')}
          confirmLabel={confirming === 'resend' ? t('listings.resend') : t('listings.importAll')}
          onConfirm={() => (confirming === 'resend' ? resend.mutate(ids) : act('import', { all: true }))}
          onClose={() => setConfirming(null)}
          isBusy={resend.isPending || change.isPending}
          error={confirming === 'resend' ? resend.error : change.error}
        />
      ) : null}
    </Paper>
  );
};
